import React, { useState, useEffect, useCallback, memo } from "react";
import { Select, Table, Spin, Alert, Button } from "antd";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import {
  getGroups,
  getFacilitiesByGroup,
  getTodayNext7Days,
  getCompletionTrend,
  getStatusMix,
  getFrequencyDistribution,
  getTopOverdueFacilities,
} from "./periodicalApi";
import "./Dashboard.css";

const { Option } = Select;

// Same as the old app: the current year and the next 5.
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => String(moment().year() + i));
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const STATUS_MIX = [
  { key: "scheduled", label: "Scheduled", color: "#1f77b4" },
  { key: "assigned", label: "Assigned", color: "#ff7f0e" },
  { key: "in_progress", label: "In Progress", color: "#9467bd" },
  { key: "completed", label: "Completed", color: "#2ca02c" },
  { key: "cancelled", label: "Cancelled", color: "#d62728" },
  { key: "overdue", label: "Overdue", color: "#e377c2" },
];
// Shown when the API sends no per-frequency details.
const FREQUENCY_SUMMARY = [
  { key: "daily", name: "Daily" },
  { key: "weekly", name: "Weekly" },
  { key: "monthly", name: "Monthly" },
  { key: "quarterly", name: "Quarterly" },
  { key: "half_yearly", name: "Half Yearly" },
  { key: "yearly", name: "Yearly" },
];

const badgeClass = (status = "") => {
  const s = status.toLowerCase();
  if (s.includes("completed")) return "completed";
  if (s.includes("overdue")) return "overdue";
  if (s.includes("in progress")) return "in-progress";
  if (s.includes("assigned")) return "assigned";
  if (s.includes("cancelled")) return "cancelled";
  return "scheduled";
};

const taskColumns = [
  {
    title: "Date",
    dataIndex: "planned_start_date_time",
    key: "planned_start_date_time",
    width: "20%",
    render: (v) => (v ? moment(v).format("D MMM YYYY") : "No date"),
  },
  { title: "Facility", dataIndex: "contract_facility_name", key: "contract_facility_name", width: "30%" },
  { title: "Periodical", dataIndex: "periodical_name", key: "periodical_name", width: "30%" },
  {
    title: "Status",
    dataIndex: "status_name",
    key: "status_name",
    width: "20%",
    render: (v) => <span className={`ws-dash-badge ws-dash-badge--${badgeClass(v)}`}>{v || "Unknown"}</span>,
  },
];

const DashCard = ({ title, subtitle, children }) => (
  <div className="ws-dash-card">
    <div className="ws-dash-card-header">
      <h5 className="ws-dash-card-title">{title}</h5>
      <p className="ws-dash-card-subtitle">{subtitle}</p>
    </div>
    <div className="ws-dash-card-body">{children}</div>
  </div>
);

const BarList = ({ items, variant }) => {
  const max = Math.max(0, ...items.map((item) => item.count));
  return (
    <div className="ws-dash-bars">
      {items.map((item) => (
        <div key={item.key} className={`ws-dash-bar-item ws-dash-bar-item--${variant}`}>
          <div className="ws-dash-bar-name" title={item.name}>{item.name}</div>
          <div className="ws-dash-bar-count">{item.count}</div>
          <div className="ws-dash-bar-track">
            <div className="ws-dash-bar-fill" style={{ width: `${max > 0 ? (item.count / max) * 100 : 0}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

// Laid out like the old app's Overview screen (dashboard_overview.js). It is
// org-wide: Group "0" means All Groups, and Facility optionally narrows to one
// facility within the selected group.
const Dashboard = () => {
  const [groups, setGroups] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [orgId, setOrgId] = useState("0");
  const [facilityId, setFacilityId] = useState("");
  const [year, setYear] = useState(YEAR_OPTIONS[0]);
  const [taskTab, setTaskTab] = useState("today");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [todayNext7, setTodayNext7] = useState(null);
  const [trend, setTrend] = useState([]);
  const [statusMix, setStatusMix] = useState(null);
  const [freqDist, setFreqDist] = useState(null);
  const [topOverdue, setTopOverdue] = useState([]);

  useEffect(() => {
    (async () => {
      const { error, result } = await getGroups();
      setGroups(!error && Array.isArray(result?.result) ? result.result : []);
    })();
  }, []);

  const onOrgChange = useCallback(async (value) => {
    setOrgId(value);
    setFacilityId("");
    setFacilities([]);
    if (!value || value === "0") return;
    const { error, result } = await getFacilitiesByGroup(value);
    setFacilities(!error && Array.isArray(result?.result) ? result.result : []);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const opts = { year, orgId, facilityIds: facilityId };
    const [tn7Res, trendRes, mixRes, freqRes, overdueRes] = await Promise.all([
      getTodayNext7Days(opts),
      getCompletionTrend(opts),
      getStatusMix(opts),
      getFrequencyDistribution(opts),
      getTopOverdueFacilities(opts),
    ]);
    setTodayNext7(!tn7Res.error ? tn7Res.result?.result : null);
    setTrend(!trendRes.error && Array.isArray(trendRes.result?.result) ? trendRes.result.result : []);
    setStatusMix(!mixRes.error ? mixRes.result?.result : null);
    setFreqDist(!freqRes.error ? freqRes.result?.result : null);
    setTopOverdue(!overdueRes.error && Array.isArray(overdueRes.result?.result) ? overdueRes.result.result : []);
    setLoadError(tn7Res.error && trendRes.error && mixRes.error && freqRes.error && overdueRes.error);
    setLoading(false);
  }, [year, orgId, facilityId]);

  useEffect(() => { load(); }, [load]);

  if (loadError) {
    return (
      <div>
        <Alert
          type="error"
          showIcon
          message="Couldn't load Work Scheduler dashboard"
          description="The Work Scheduler service didn't respond. Try again, or check back shortly."
        />
        <Button size="small" style={{ marginTop: 12 }} onClick={load}>Retry</Button>
      </div>
    );
  }

  // A missing count (empty or partial API result) is shown as 0.
  const count = (obj, key) => Math.max(0, Number(obj?.[key]) || 0);
  const dueToday = todayNext7?.due_today;
  const next7 = todayNext7?.next_7_days;

  // All 12 months, even ones the API leaves out.
  const trendByMonth = MONTHS.map((_, i) => trend.find((t) => Number(t.month) === i + 1) || {});
  const trendOptions = {
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { columnWidth: "70%", borderRadius: 4 } },
    xaxis: { categories: MONTHS },
    yaxis: { forceNiceScale: true, labels: { formatter: (v) => Math.round(v) } },
    dataLabels: { enabled: false },
    grid: { strokeDashArray: 3 },
    colors: ["#1f77b4", "#2ca02c", "#d62728"],
    legend: { position: "bottom" },
  };
  const trendSeries = [
    { name: "Scheduled", data: trendByMonth.map((t) => count(t, "total_scheduled")) },
    { name: "Completed", data: trendByMonth.map((t) => count(t, "completed")) },
    { name: "Overdue", data: trendByMonth.map((t) => count(t, "overdue")) },
  ];

  const mix = STATUS_MIX.map((s) => ({ ...s, value: count(statusMix, s.key) })).filter((s) => s.value > 0);
  const mixOptions = {
    chart: { type: "donut" },
    labels: mix.map((s) => s.label),
    colors: mix.map((s) => s.color),
    legend: { show: false },
    stroke: { width: 3 },
    dataLabels: {
      formatter: (percent) => (percent < 3 ? "" : `${Math.round(percent)}%`),
      dropShadow: { enabled: true },
      style: { fontSize: "14px", fontWeight: 700 },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "68%",
          labels: {
            show: true,
            value: { fontSize: "28px", fontWeight: 700, color: "#1a1a1a", offsetY: 4 },
            total: { show: true, showAlways: true, label: "Total Tasks", fontSize: "12px", color: "#999" },
          },
        },
      },
    },
  };

  const freqDetails = Array.isArray(freqDist?.details)
    ? freqDist.details.filter((d) => d?.frequency_name && Number(d.count) > 0)
    : [];
  const freqItems = freqDetails.length
    ? freqDetails.map((d, i) => ({ key: i, name: String(d.frequency_name).trim(), count: count(d, "count") }))
    : FREQUENCY_SUMMARY.map((f) => ({ key: f.key, name: f.name, count: count(freqDist, f.key) }));

  const facilityItems = topOverdue
    .filter((f) => f?.facility_name)
    .slice(0, 5)
    .map((f, i) => ({ key: f.facility_id || i, name: String(f.facility_name).trim(), count: count(f, "total_overdue") }));

  return (
    <Spin spinning={loading}>
      <div className="ws-dash">
        <div className="ws-dash-filters">
          <div>
            <label className="ws-dash-filter-label">Group</label>
            <Select value={orgId} onChange={onOrgChange} style={{ width: 200 }}>
              <Option value="0">All Group</Option>
              {groups.map((g) => <Option key={g.id} value={String(g.id)}>{g.name}</Option>)}
            </Select>
          </div>
          <div>
            <label className="ws-dash-filter-label">Facility</label>
            <Select value={facilityId} onChange={setFacilityId} style={{ width: 200 }} disabled={orgId === "0"}>
              <Option value="">All Facility</Option>
              {facilities.map((f) => <Option key={f.facility_id ?? f.id} value={String(f.facility_id ?? f.id)}>{f.facility_name ?? f.name}</Option>)}
            </Select>
          </div>
          <div>
            <label className="ws-dash-filter-label">Month / Year</label>
            <Select value={year} onChange={setYear} style={{ width: 140 }}>
              {YEAR_OPTIONS.map((y) => <Option key={y} value={y}>{y}</Option>)}
            </Select>
          </div>
        </div>

        <div className="ws-dash-stats">
          {[
            ["scheduled", "Scheduled"],
            ["assigned", "Assigned"],
            ["completed", "Completed"],
            ["overdue", "Overdue"],
          ].map(([key, label]) => (
            <div key={key} className={`ws-dash-stat ws-dash-stat--${key}`}>
              <div className="ws-dash-stat-label">{label}</div>
              <div className="ws-dash-stat-number">{count(statusMix, key)}</div>
            </div>
          ))}
        </div>

        <div className="ws-dash-row ws-dash-row--wide ws-dash-row--top">
          <DashCard title="Due Today | Next 7 Days" subtitle="Immediate work visibility for supervisors">
            <div className="ws-dash-task-tabs">
              <div className={`ws-dash-task-tab ${taskTab === "today" ? "active" : ""}`} onClick={() => setTaskTab("today")}>
                <div className="ws-dash-task-tab-label">Due Today</div>
                <div className="ws-dash-task-tab-number">{count(dueToday?.summary, "total_due_today")}</div>
                <div className="ws-dash-task-tab-detail">{count(dueToday?.summary, "overdue_today")} overdue</div>
              </div>
              <div className={`ws-dash-task-tab ${taskTab === "next7days" ? "active" : ""}`} onClick={() => setTaskTab("next7days")}>
                <div className="ws-dash-task-tab-label">Next 7 Days</div>
                <div className="ws-dash-task-tab-number">{count(next7, "total_upcoming")}</div>
                <div className="ws-dash-task-tab-detail">Upcoming tasks</div>
              </div>
            </div>
            <div className="ws-dash-task-table">
              <Table
                rowKey={(row, i) => row.id || i}
                columns={taskColumns}
                dataSource={(taskTab === "today" ? dueToday?.tasks : next7?.tasks) || []}
                pagination={false}
                size="small"
                locale={{ emptyText: taskTab === "today" ? "No tasks due today" : "No tasks in the next 7 days" }}
              />
            </div>
          </DashCard>

          <DashCard title="Attention Required" subtitle="Critical items needing action">
            <div className="ws-dash-attention">
              {[
                ["overdue", "error", "overdue periodicals", "Require immediate action"],
                ["scheduled", "warning", "unassigned scheduled tasks", "Require allocation before next cycle"],
                ["in_progress", "info", "tasks in progress", "Monitor for timely completion"],
                ["cancelled", "danger", "cancelled tasks", "Review and reschedule if needed"],
              ].map(([key, tone, text, detail]) => (
                <div key={key} className={`ws-dash-attention-item ${count(statusMix, key) > 0 ? `ws-dash-attention-item--${tone}` : ""}`}>
                  <div className="ws-dash-attention-dot" />
                  <div>
                    <div className="ws-dash-attention-main">{count(statusMix, key)} {text}</div>
                    <div className="ws-dash-attention-detail">{detail}</div>
                  </div>
                </div>
              ))}
            </div>
          </DashCard>
        </div>

        <div className="ws-dash-row ws-dash-row--wide">
          <DashCard title="Completion Trend" subtitle="Monthly scheduled vs completed vs overdue">
            {trend.length ? (
              <ReactApexChart options={trendOptions} series={trendSeries} type="bar" height={350} />
            ) : (
              <div className="ws-dash-empty">No completion trend data available</div>
            )}
          </DashCard>

          <DashCard title="Status Mix" subtitle="Distribution of task statuses">
            {mix.length ? (
              <>
                <ReactApexChart options={mixOptions} series={mix.map((s) => s.value)} type="donut" height={300} />
                <div className="ws-dash-mix-legend">
                  {mix.map((s) => (
                    <div key={s.key} className="ws-dash-mix-tile">
                      <span className="ws-dash-mix-swatch" style={{ backgroundColor: s.color }} />
                      <div className="ws-dash-mix-label">{s.label}</div>
                      <div className="ws-dash-mix-value">{s.value}</div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="ws-dash-empty">No status data available</div>
            )}
          </DashCard>
        </div>

        <div className="ws-dash-row ws-dash-row--half">
          <DashCard title="Frequency Distribution" subtitle="Work spread across frequency types">
            <BarList items={freqItems} variant="frequency" />
          </DashCard>

          <DashCard title="Top Facilities by Overdue Work" subtitle="Top 5 facilities with highest overdue tasks">
            {facilityItems.length ? (
              <BarList items={facilityItems} variant="facility" />
            ) : (
              <div className="ws-dash-empty">No overdue facilities data available</div>
            )}
          </DashCard>
        </div>
      </div>
    </Spin>
  );
};

export default memo(Dashboard);
